import React, { useEffect, useMemo, useState } from "react";
import { Download, CheckSquare, Square, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { generatePayrollPDF } from "@/lib/pdf";
import OfficialPayrollRow from "@/components/OfficialPayrollRow";
import HelpHint from "@/components/HelpHint";
import SortHeader, { useSort } from "@/components/SortHeader";

export default function PayrollPage() {
  const [events, setEvents] = useState([]);
  const [officials, setOfficials] = useState([]);
  const [grades, setGrades] = useState([]);
  const [settings, setSettings] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extras, setExtras] = useState([]);
  const [payments, setPayments] = useState([]); // [{event_id, official_id, paid_at}]

  // Per-column filters
  const [fOfficial, setFOfficial] = useState("");
  const [fParties, setFParties] = useState(""); // numeric "min" filter
  const [fGrade, setFGrade] = useState(""); // not directly shown but kept for future
  const [fStatus, setFStatus] = useState(""); // "" | "paid" | "unpaid"
  const [hidePaid, setHidePaid] = useState(false);

  // Excel-like column sort
  const { sortKey, sortDir, cycleSort } = useSort(null);

  useEffect(() => {
    (async () => {
      try {
        const [ev, off, gr, st] = await Promise.all([
          api.get("/events?include_archived=1"),
          api.get("/officials"),
          api.get("/grades"),
          api.get("/settings"),
        ]);
        setEvents(ev.data.events);
        setOfficials(off.data.officials);
        setGrades(gr.data.grades);
        setSettings(st.data.settings);
        const closed = ev.data.events.filter((e) => e.closed);
        if (closed.length > 0) setSelectedId(closed[0].id);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setExtras([]);
      setPayments([]);
      return;
    }
    (async () => {
      try {
        const [exRes, payRes] = await Promise.all([
          api.get(`/events/${selectedId}/extras`),
          api.get(`/events/${selectedId}/payments`),
        ]);
        setExtras(exRes.data.extras || []);
        setPayments(payRes.data.payments || []);
      } catch {
        setExtras([]);
        setPayments([]);
      }
    })();
  }, [selectedId]);

  const reloadExtras = async () => {
    if (!selectedId) return;
    try {
      const { data } = await api.get(`/events/${selectedId}/extras`);
      setExtras(data.extras || []);
    } catch {
      // ignore
    }
  };

  const togglePaid = async (officialId) => {
    try {
      const { data } = await api.post(`/events/${selectedId}/payments/${officialId}`);
      if (data.paid) {
        setPayments((p) => [...p, { event_id: selectedId, official_id: officialId, paid_at: data.paid_at }]);
        toast.success("Marqué comme payé");
      } else {
        setPayments((p) => p.filter((x) => x.official_id !== officialId));
        toast.success("Marqué comme non payé");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const isPaid = (officialId) => payments.some((p) => p.official_id === officialId);
  const paidAt = (officialId) => payments.find((p) => p.official_id === officialId)?.paid_at || null;

  const closedEvents = events.filter((e) => e.closed);
  const event = closedEvents.find((e) => e.id === selectedId);

  const payroll = useMemo(() => {
    if (!event) return [];
    const perOff = {};
    (event.games || []).forEach((g) => {
      if (g.is_break) return;
      [
        ["referee1_id", "ref"],
        ["referee2_id", "ref"],
        ["scorer_id", "sc"],
      ].forEach(([field, type]) => {
        if (!g[field]) return;
        const off = officials.find((o) => o.id === g[field]);
        if (!off) return;
        const grade =
          type === "ref"
            ? grades.find((gr) => gr.id === off.referee_grade_id && gr.type === "referee")
            : grades.find((gr) => gr.id === off.scorer_grade_id && gr.type === "scorer");
        if (!perOff[off.id]) perOff[off.id] = { off, official: off, refGrade: null, scGrade: null, gameLines: [], extras: [], deductions: [], basePay: 0 };
        if (type === "ref") perOff[off.id].refGrade = grade;
        else perOff[off.id].scGrade = grade;
        const amount = Number(grade?.rate || 0);
        perOff[off.id].gameLines.push({
          game: g,
          role: type === "ref" ? "Arbitre" : "Marqueur",
          grade: grade?.name || "—",
          amount,
        });
        perOff[off.id].basePay += amount;
      });
    });
    (extras || []).forEach((x) => {
      let entry = perOff[x.official_id];
      if (!entry) {
        const off = officials.find((o) => o.id === x.official_id);
        if (!off) return;
        entry = perOff[off.id] = { off, official: off, refGrade: null, scGrade: null, gameLines: [], extras: [], deductions: [], basePay: 0 };
      }
      if (x.kind === "extra") entry.extras.push(x);
      else if (x.kind === "deduction") entry.deductions.push(x);
    });
    return Object.values(perOff).map((p) => {
      const exSum = p.extras.reduce((s, x) => s + Number(x.amount || 0), 0);
      const dedSum = p.deductions.reduce((s, x) => s + Number(x.amount || 0), 0);
      return { ...p, games: p.gameLines.length, total: p.basePay + exSum - dedSum, exSum, dedSum };
    }).sort((a, b) =>
      `${a.off.first_name} ${a.off.last_name}`.localeCompare(`${b.off.first_name} ${b.off.last_name}`, "fr", { sensitivity: "base" })
    );
  }, [event, officials, grades, extras]);

  // Apply per-column filters
  const filtered = payroll.filter((p) => {
    if (fOfficial) {
      const name = `${p.off.first_name} ${p.off.last_name}`.toLowerCase();
      if (!name.includes(fOfficial.toLowerCase())) return false;
    }
    if (fParties) {
      const min = parseInt(fParties, 10);
      if (Number.isFinite(min) && p.games < min) return false;
    }
    if (fGrade) {
      const g = `${p.refGrade?.name || ""} ${p.scGrade?.name || ""}`.toLowerCase();
      if (!g.includes(fGrade.toLowerCase())) return false;
    }
    if (fStatus === "paid" && !isPaid(p.off.id)) return false;
    if (fStatus === "unpaid" && isPaid(p.off.id)) return false;
    if (hidePaid && isPaid(p.off.id)) return false;
    return true;
  });

  // Apply column sort
  const sortedFiltered = [...filtered].sort((a, b) => {
    if (!sortKey) return 0;
    let va, vb;
    switch (sortKey) {
      case "official":
        va = `${a.off.first_name} ${a.off.last_name}`.toLowerCase();
        vb = `${b.off.first_name} ${b.off.last_name}`.toLowerCase();
        break;
      case "games": va = a.games; vb = b.games; break;
      case "basePay": va = a.basePay; vb = b.basePay; break;
      case "exSum": va = a.exSum; vb = b.exSum; break;
      case "dedSum": va = a.dedSum; vb = b.dedSum; break;
      case "total": va = a.total; vb = b.total; break;
      case "paid": va = isPaid(a.off.id) ? 1 : 0; vb = isPaid(b.off.id) ? 1 : 0; break;
      default: return 0;
    }
    if (typeof va === "string") {
      const cmp = va.localeCompare(vb, "fr", { sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    }
    return sortDir === "asc" ? va - vb : vb - va;
  });

  const grandTotal = filtered.reduce((s, p) => s + p.total, 0);
  const grandBase = filtered.reduce((s, p) => s + p.basePay, 0);
  const grandExtras = filtered.reduce((s, p) => s + p.exSum, 0);
  const grandDeductions = filtered.reduce((s, p) => s + p.dedSum, 0);

  const paidCount = payroll.filter((p) => isPaid(p.off.id)).length;
  const anyFilter = !!(fOfficial || fParties || fGrade || fStatus || hidePaid);
  const resetFilters = () => {
    setFOfficial("");
    setFParties("");
    setFGrade("");
    setFStatus("");
    setHidePaid(false);
  };

  return (
    <div data-testid="payroll-page">
      <div className="mb-10">
        <div className="section-number">03 / Paies</div>
        <h1 className="h1-big">Tes paiements.</h1>
        <p className="text-sm text-[var(--muted)] mt-3 max-w-xl leading-relaxed">
          Une fois un événement fermé, la paie de chaque officiel est calculée automatiquement selon les grades attribués.
        </p>
      </div>

      {loading ? (
        <p className="font-mono text-xs uppercase tracking-widest text-[var(--muted)] alert-pulse">Chargement…</p>
      ) : closedEvents.length === 0 ? (
        <div className="border-2 border-dashed border-[var(--border-strong)] p-16 text-center">
          <p className="font-serif text-3xl italic mb-2">Aucun événement fermé.</p>
          <p className="font-mono text-xs uppercase tracking-widest text-[var(--muted)]">Ferme un événement pour générer les paies.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-[var(--border)]">
            {closedEvents.map((e) => (
              <button
                key={e.id}
                onClick={() => setSelectedId(e.id)}
                className={`px-3 py-2 text-xs font-mono uppercase tracking-wider border ${
                  selectedId === e.id ? "bg-black text-white border-black" : "border-[var(--border-strong)] text-[var(--muted)] hover:border-black hover:text-black"
                }`}
                data-testid={`payroll-event-${e.id}`}
              >
                {e.name} · {e.date}
              </button>
            ))}
          </div>

          {event && (
            <>
              <div className="flex items-center justify-between mb-6 gap-4">
                <div>
                  <div className="label">Total à payer (filtré)</div>
                  <div className="font-serif text-6xl tabular leading-none">{grandTotal.toFixed(2)} $</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mt-2">
                    {paidCount}/{payroll.length} officiel{payroll.length > 1 ? "s" : ""} payé{paidCount > 1 ? "s" : ""}
                  </div>
                </div>
                <span className="inline-flex items-center">
                  <button
                    onClick={() => generatePayrollPDF(event, payroll, payroll.reduce((s, p) => s + p.total, 0), settings)}
                    className="btn-primary"
                    data-testid="payroll-pdf-btn"
                  >
                    <Download size={14} strokeWidth={2.5} /> PDF Paies
                  </button>
                  <HelpHint
                    title="PDF Paies"
                    text="Génère un PDF imprimable contenant la feuille de paie détaillée de chaque officiel (base + extras − déductions) ainsi qu'un sommaire total. Idéal pour les remettre aux officiels ou pour les archives."
                    testid="hint-payroll-pdf"
                  />
                </span>
              </div>

              {payroll.length === 0 ? (
                <p className="text-sm text-[var(--muted)]">Aucun officiel assigné sur cet événement.</p>
              ) : (
                <div className="card-flat" data-testid="payroll-breakdown">
                  <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                    <div className="label">Paie ventilée par officiel</div>
                    <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="hide-paid-toggle">
                      <input
                        type="checkbox"
                        checked={hidePaid}
                        onChange={(e) => setHidePaid(e.target.checked)}
                        className="w-4 h-4 border-2 border-black"
                      />
                      <span className="font-mono uppercase tracking-widest text-[10px]">Cacher les payés</span>
                    </label>
                  </div>
                  <p className="font-mono text-[10px] text-[var(--muted)] mb-2 leading-relaxed">
                    Ajoute des <b>extras</b> (payés + facturés au client, ex: essence) ou <b>déductions</b> (paie seulement, ex: commande de vêtements) directement sur la ligne de chaque officiel.
                  </p>
                  {anyFilter && (
                    <div className="mb-3">
                      <button
                        onClick={resetFilters}
                        className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] hover:text-black inline-flex items-center gap-1"
                        data-testid="reset-payroll-filters"
                      >
                        <X size={12} strokeWidth={2.5} /> Réinitialiser les filtres ({filtered.length}/{payroll.length})
                      </button>
                    </div>
                  )}
                  <div className="border-t-2 border-b-2 border-black overflow-x-auto">
                    <table className="w-full text-xs" data-testid="payroll-breakdown-table">
                      <thead className="sticky-th">
                        <tr className="border-b border-black">
                          <SortHeader k="official" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort}>Officiel</SortHeader>
                          <SortHeader k="games" align="right" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort}>Parties</SortHeader>
                          <SortHeader k="basePay" align="right" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort}>Paie base</SortHeader>
                          <SortHeader k="exSum" align="right" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort}>Extras&nbsp;(+)</SortHeader>
                          <SortHeader k="dedSum" align="right" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort}>Déductions&nbsp;(−)</SortHeader>
                          <SortHeader k="total" align="right" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort}>Net</SortHeader>
                          <SortHeader k="paid" align="center" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort}>Payé</SortHeader>
                          <th className="py-2 px-2" />
                        </tr>
                        <tr className="border-b-2 border-black bg-[var(--surface-3)]">
                          <th className="py-1.5 px-2">
                            <input
                              value={fOfficial}
                              onChange={(e) => setFOfficial(e.target.value)}
                              placeholder="Filtrer…"
                              className="w-full bg-transparent text-xs px-1 py-0.5 border border-[var(--border-strong)]"
                              data-testid="filter-official"
                            />
                          </th>
                          <th className="py-1.5 px-2">
                            <input
                              type="number"
                              value={fParties}
                              onChange={(e) => setFParties(e.target.value)}
                              placeholder="≥ N"
                              min="0"
                              className="w-full bg-transparent text-xs px-1 py-0.5 border border-[var(--border-strong)] text-right tabular"
                              data-testid="filter-parties"
                            />
                          </th>
                          <th />
                          <th />
                          <th />
                          <th />
                          <th className="py-1.5 px-2">
                            <select
                              value={fStatus}
                              onChange={(e) => setFStatus(e.target.value)}
                              className="w-full bg-transparent text-xs px-1 py-0.5 border border-[var(--border-strong)]"
                              data-testid="filter-status"
                            >
                              <option value="">Tous</option>
                              <option value="paid">Payé</option>
                              <option value="unpaid">Non payé</option>
                            </select>
                          </th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {sortedFiltered.map((p) => {
                          const paid = isPaid(p.off.id);
                          const at = paidAt(p.off.id);
                          const paidCol = (
                            <td className="py-2 px-2 text-center" key={`paid-${p.off.id}`}>
                              <button
                                onClick={() => togglePaid(p.off.id)}
                                className="inline-flex flex-col items-center gap-0.5 group"
                                title={paid && at ? `Payé le ${new Date(at).toLocaleDateString("fr-CA")} ${new Date(at).toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" })}` : "Marquer comme payé"}
                                data-testid={`paid-toggle-${p.off.id}`}
                              >
                                {paid ? (
                                  <CheckSquare size={18} strokeWidth={2.5} className="text-black" />
                                ) : (
                                  <Square size={18} strokeWidth={2} className="text-[var(--muted)] group-hover:text-black" />
                                )}
                                {paid && at && (
                                  <span className="font-mono text-[9px] uppercase tracking-wider text-[var(--muted)] block">
                                    {new Date(at).toLocaleDateString("fr-CA")}
                                  </span>
                                )}
                              </button>
                            </td>
                          );
                          return (
                            <OfficialPayrollRow
                              key={p.off.id}
                              row={p}
                              net={p.total}
                              exSum={p.exSum}
                              dedSum={p.dedSum}
                              eventId={event.id}
                              onChanged={reloadExtras}
                              extraCols={paidCol}
                              extraColSpan={8}
                            />
                          );
                        })}
                        <tr className="border-t-2 border-black bg-[var(--surface-2)]" data-testid="payroll-totals-row">
                          <td className="py-2 px-2 font-bold uppercase tracking-widest text-[10px]">Totaux (filtrés)</td>
                          <td className="py-2 px-2 text-right tabular font-bold">{filtered.reduce((s, p) => s + p.games, 0)}</td>
                          <td className="py-2 px-2 text-right tabular font-bold">{grandBase.toFixed(2)} $</td>
                          <td className="py-2 px-2 text-right tabular font-bold">+{grandExtras.toFixed(2)} $</td>
                          <td className="py-2 px-2 text-right tabular font-bold">−{grandDeductions.toFixed(2)} $</td>
                          <td className="py-2 px-2 text-right tabular font-bold">{grandTotal.toFixed(2)} $</td>
                          <td />
                          <td />
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
